import React, { useMemo, useState } from 'react';
import { Avatar, Dropdown, Modal, Tag, Descriptions } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { UserOutlined, LogoutOutlined, SettingOutlined, InfoCircleOutlined, BranchesOutlined, CodeOutlined, CalendarOutlined, CloudServerOutlined } from '@ant-design/icons';
import { useAuth, AuthenticatedTemplate, UnauthenticatedTemplate } from '../../../hooks/useAuth';
import { useRbac } from '../../../contexts/RbacContext';
import { RBAC_PERMISSIONS } from '../../../config/rbac';
import { getRoleLabel, getUserInitials } from '../../../utils/user';
import { getStoredImpersonation } from '../../../features/impersonation/impersonationStorage';
import { useWebSocket } from '../../../hooks/useWebSocket';
import LoginButton from '../../auth/LoginButton';

/**
 * Меню пользователя в хедере.
 * Показывает аватар, dropdown с профилем, ролями, настройками и кнопкой выхода.
 */
const wsStatusColorMap: Record<string, string> = {
  CONNECTED: '#52c41a',
  CONNECTING: '#faad14',
  RECONNECTING: '#faad14',
  DISCONNECTED: '#ff4d4f',
  ERROR: '#ff4d4f',
};

const wsStatusTextMap: Record<string, string> = {
  CONNECTED: 'Онлайн',
  CONNECTING: 'Подключение...',
  RECONNECTING: 'Переподключение...',
  DISCONNECTED: 'Оффлайн',
  ERROR: 'Ошибка соединения',
};

export const UserMenu: React.FC = () => {
  const { user, userPhoto, logout, roles } = useAuth();
  const { hasPermission, currentUser } = useRbac();
  const { status: wsStatus } = useWebSocket();
  const navigate = useNavigate();
  const [aboutOpen, setAboutOpen] = useState(false);

  // Под эмуляцией ("Войти как") useAuth отдаёт актора, а работа идёт от имени
  // цели — источником истины для профиля должен быть RBAC-пользователь.
  // Фото тоже принадлежит актору (MSAL Graph), поэтому под эмуляцией
  // показываем инициалы цели, а не лицо администратора.
  const isImpersonating = !!getStoredImpersonation();
  const avatarPhoto = isImpersonating ? undefined : userPhoto;
  const displayName = currentUser?.user.displayName || user?.displayName || 'Пользователь';
  const displayEmail =
    currentUser?.user.email || user?.userPrincipalName || user?.mail || 'email@medipal.tech';

  const roleLabels = useMemo(
    () =>
      currentUser
        ? currentUser.roles.map((r) => r.name || getRoleLabel(r.value))
        : roles.map(getRoleLabel),
    [currentUser, roles],
  );

  const canAccessAdmin = useMemo(
    () => hasPermission('admin', RBAC_PERMISSIONS.MANAGE),
    [hasPermission]
  );

  const userMenuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: (
          <div style={{ padding: '8px 0', minWidth: '200px' }}>
            <div style={{
              fontWeight: '600',
              fontSize: '14px',
              color: 'var(--color-text-primary)',
              marginBottom: '4px',
              wordBreak: 'break-word'
            }}>
              {displayName}
            </div>
            <div style={{
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
              lineHeight: '1.2',
              wordBreak: 'break-word'
            }}>
              {displayEmail}
            </div>
            {roleLabels.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {roleLabels.map(label => (
                  <Tag key={label} color={label === 'Администратор' ? 'geekblue' : 'default'} style={{ marginRight: 4 }}>
                    {label}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        ),
        disabled: true,
      },
      { type: 'divider' },
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: 'Настройки',
        onClick: () => navigate('/settings'),
      },
    ];

    if (canAccessAdmin) {
      items.push(
        { type: 'divider' },
        {
          key: 'admin',
          icon: <SettingOutlined />,
          label: 'Админ-панель',
          onClick: () => navigate('/admin'),
        }
      );
    }

    items.push(
      { type: 'divider' },
      {
        key: 'about',
        icon: <InfoCircleOutlined />,
        label: 'О приложении',
        onClick: () => setAboutOpen(true),
      },
      { type: 'divider' },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Выйти',
        onClick: logout,
      }
    );

    return items;
  }, [displayName, displayEmail, roleLabels, logout, canAccessAdmin, navigate]);

  return (
    <>
      <AuthenticatedTemplate>
        <div data-tour-id="user-profile">
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <div style={{ position: 'relative', display: 'inline-flex', cursor: 'pointer' }}>
              <Avatar
                src={avatarPhoto}
                icon={<UserOutlined />}
                size={32}
              >
                {!avatarPhoto && getUserInitials(displayEmail)}
              </Avatar>
              <span
                title={wsStatusTextMap[wsStatus] ?? 'Неизвестно'}
                style={{
                  position: 'absolute',
                  bottom: -1,
                  right: -1,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: wsStatusColorMap[wsStatus] ?? '#ff4d4f',
                  boxShadow: '0 0 0 2px #172554',
                  zIndex: 1011,
                  pointerEvents: 'none',
                }}
              />
            </div>
          </Dropdown>
        </div>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <LoginButton />
      </UnauthenticatedTemplate>

      <Modal
        title="О приложении"
        open={aboutOpen}
        onCancel={() => setAboutOpen(false)}
        footer={null}
        width={480}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--color-primary-light)', marginBottom: 4 }}>PLNG</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-disabled)' }}>Система планирования продаж и закупок</div>
        </div>

        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label={<><CloudServerOutlined /> Режим</>}>
            {import.meta.env.MODE === 'development' ? (
              <Tag color="warning">Development</Tag>
            ) : (
              <Tag color="success">Production</Tag>
            )}
          </Descriptions.Item>
          {import.meta.env.MODE === 'development' && (
            <>
              <Descriptions.Item label={<><BranchesOutlined /> Ветка</>}>
                {__APP_GIT_BRANCH__}
              </Descriptions.Item>
              <Descriptions.Item label={<><CodeOutlined /> Коммит</>}>
                <code style={{ fontSize: 12 }}>{__APP_GIT_COMMIT_HASH__}</code>
              </Descriptions.Item>
              <Descriptions.Item label={<><CalendarOutlined /> Дата коммита</>}>
                {__APP_GIT_COMMIT_DATE__}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Modal>
    </>
  );
};
